import { AFFILIATE } from '~/constants/affiliate'

/**
 * What each plan may do, as the server enforces it. Kept free of server
 * imports: server/utils/rateLimit.ts and the endpoints meter with these
 * numbers, and the pricing page's comparison (constants/planComparison.ts)
 * prints the same ones, so the page cannot promise a limit the app does not
 * apply.
 */
export type QuotaScope = 'ai-import' | 'pdf-export' | 'pdf-preview' | 'guest-export' | 'job-match' | 'ai-writing' | 'ai-rewrite' | 'ai-about-me' | 'ai-chat' | 'ai-chat-build' | 'ai-skill-line' | 'ats-upload' | 'ats-check' | 'cv-translate' | 'cover-letter' | 'application-email' | 'interview-prep' | 'interview-prep-offer' | 'interview-prep-preview' | 'civil-service-read' | 'civil-service-ask' | 'civil-service-draft' | 'civil-service-fill' | 'civil-service-try' | 'civil-service-save-email' | 'nhs-read' | 'nhs-ask' | 'nhs-draft' | 'nhs-fill' | 'nhs-try' | 'affiliate-click' | 'newsletter-signup' | 'cv-email' | 'posting-url' | 'cv-file' | 'link-view'

export interface ScopeLimits {
  anonymous: number
  free: number
  /**
   * Fair-use ceiling for the pack and subscriptions, or null for no metering.
   * Set well above what one person applying for jobs needs, so only a script
   * or a shared account ever meets it; it keeps one $29 pack from running up
   * an unbounded AI bill.
   */
  premium: number | null
}

/** Daily limits per scope. A 0 outside premium means the feature is premium only. */
export const DAILY_LIMITS: Record<QuotaScope, ScopeLimits> = {
  'ai-import': { anonymous: 2, free: 3, premium: 30 },
  'pdf-export': { anonymous: 3, free: 10, premium: null },
  // The preview is the PDF itself, printed by Chrome (server/api/pdf/preview.post.ts):
  // one unit per render the cache could not answer, i.e. per pause in typing.
  // Generous on purpose — it only stops a script hammering Chrome.
  'pdf-preview': { anonymous: 200, free: 800, premium: null },
  // The free job match is a preview (see FREE_PLAN.reportItems). Shared with
  // the editor's tailoring (/api/ai/tailor-cv, account only), whose free run is
  // a match only, and with the tailoring preview under a CV just built
  // (/api/ai/tailor-preview), the one use open to signed-out visitors.
  'job-match': { anonymous: 1, free: 1, premium: 60 },
  // Editor AI suggestions (/api/ai/generate-suggestions).
  'ai-writing': { anonymous: 2, free: 3, premium: 150 },
  // Rewrites of a CV section (/api/ai/enhance-content): premium only.
  'ai-rewrite': { anonymous: 0, free: 0, premium: 150 },
  // The "About me" generator: one a day on the free plan, as a taste of the
  // editor AI whose rewrites are premium.
  'ai-about-me': { anonymous: 0, free: 1, premium: 30 },
  // Parking a CV before guest checkout writes a row with no account behind it,
  // so it is metered like the generator itself. Abandoned rows are cleaned up.
  'guest-export': { anonymous: 10, free: 10, premium: null },
  // One unit per answer in the chat CV builder (/api/ai/cv-chat/message). An
  // interview is ~12-20 questions; 30 leaves room for one restart or for
  // adding to a built CV, and visitors behind one shared IP. An answer the AI
  // failed to reply to is given back. The CV built at the end is metered
  // separately as an 'ai-chat-build'.
  'ai-chat': { anonymous: 30, free: 30, premium: 300 },
  // Building the CV at the end of the chat (/api/ai/cv-chat/build). Its own
  // counter, not the paste generator's 'ai-import': whoever finished an
  // interview always gets to see their CV, even after using the generator or
  // running out of questions, which is what sells the rest. Room for a rebuild
  // after adding to it; a failed build is given back.
  'ai-chat-build': { anonymous: 3, free: 3, premium: 30 },
  // One line for a skill ticked on a tailoring, written from what the person
  // says they did with it (/api/ai/skill-line). A short call; up to ten
  // skills are offered per posting.
  'ai-skill-line': { anonymous: 10, free: 10, premium: 100 },
  // The check of an uploaded CV (/api/ai/ats-upload-check): free and
  // anonymous visitors get the score and the first findings, one AI call each.
  'ats-upload': { anonymous: 2, free: 3, premium: 50 },
  // The ATS check in the editor (/api/ai/ats-check) is premium only.
  'ats-check': { anonymous: 0, free: 0, premium: 60 },
  // Translating a whole CV into a copy in another language (/api/cv/translate).
  // The free plan gets FREE_PLAN.translations in total; this daily unit only
  // stops parallel requests from spending it twice.
  'cv-translate': { anonymous: 0, free: 1, premium: 20 },
  // Cover letter generation. The free plan's limit is a lifetime count
  // (FREE_PLAN.coverLetters), which spends only the premium ceiling here.
  'cover-letter': { anonymous: 0, free: 0, premium: 30 },
  // The application email from a CV card (/api/ai/application-email): premium only.
  'application-email': { anonymous: 0, free: 0, premium: 60 },
  // Interview prep for a posting (/api/ai/interview-prep), from the CV list or
  // the editor. A free account gets one a day as a preview: the answer to
  // "Tell me about yourself" and one question in full, the rest locked.
  'interview-prep': { anonymous: 0, free: 1, premium: 30 },
  // The interview prep offered after a tailored PDF was bought
  // (/api/guest-export/interview-prep). It is written once per purchase and
  // kept, so this only bounds retries after a failed AI call.
  'interview-prep-offer': { anonymous: 5, free: 5, premium: 5 },
  // The interview prep's preview for anyone with a CV on the result page, a
  // free download included (/api/guest-export/interview-prep with a posting).
  // Every preview is a whole prep written by the AI, so it is metered per
  // visitor like the tailoring preview, with room for a second posting.
  'interview-prep-preview': { anonymous: 2, free: 2, premium: 2 },
  // Reading a Civil Service advert into what it asks for (/api/civil-service/read):
  // one short AI call, enough for several adverts and retries.
  'civil-service-read': { anonymous: 15, free: 15, premium: 60 },
  // The questions about the applicant's examples (/api/civil-service/questions):
  // one short AI call per CV, enough for corrected CVs and other adverts.
  'civil-service-ask': { anonymous: 15, free: 15, premium: 60 },
  // Writing a whole Civil Service application for its preview
  // (/api/civil-service/draft): the statements for one advert, one or two AI
  // calls. Several adverts or tries a day per visitor; the Job Search Pack
  // includes every application, up to its fair-use ceiling.
  'civil-service-draft': { anonymous: 8, free: 8, premium: 30 },
  // Finishing a paid application with the AI: the applicant's answers put
  // into its [brackets] (/api/civil-service/<token>/fill) and changes asked
  // for in their words (/revise). One short AI call each; room for every
  // statement of an application and several goes at each.
  'civil-service-fill': { anonymous: 60, free: 60, premium: 60 },
  // The same filling tried before the payment, on the free sample statement
  // only. Counted per application (its token), not per visitor: one try on
  // any [detail] shows how it works, the rest is what the payment buys.
  'civil-service-try': { anonymous: 1, free: 1, premium: 1 },
  // "E-mail me the link" under an unpaid application, to come back to it
  // (/api/civil-service/<token>/save): each mails the address typed in, so a
  // few for typos and too few to flood somebody else's inbox.
  'civil-service-save-email': { anonymous: 5, free: 5, premium: 5 },
  // The NHS supporting statement (/api/nhs-statement/*), metered the same way
  // as the Civil Service application: reading an advert, the questions, the
  // whole statement for its preview (the Job Search Pack includes every one,
  // up to its fair-use ceiling), finishing a paid one, and one try of that on
  // the free sample, counted per statement.
  'nhs-read': { anonymous: 15, free: 15, premium: 60 },
  'nhs-ask': { anonymous: 15, free: 15, premium: 60 },
  'nhs-draft': { anonymous: 8, free: 8, premium: 30 },
  'nhs-fill': { anonymous: 60, free: 60, premium: 60 },
  'nhs-try': { anonymous: 1, free: 1, premium: 1 },
  // Clicks on affiliate links counted per visitor and day; more are ignored.
  'affiliate-click': { anonymous: AFFILIATE.clicksPerIpPerDay, free: AFFILIATE.clicksPerIpPerDay, premium: AFFILIATE.clicksPerIpPerDay },
  // Newsletter sign-ups per visitor (/api/newsletter/subscribe): each mails a
  // confirmation to the address typed in, so a few are enough for typos and
  // too few to flood somebody else's inbox.
  'newsletter-signup': { anonymous: 5, free: 5, premium: 5 },
  // "E-mail me my CV" under a generated CV (/api/guest-cv/email): same reason.
  'cv-email': { anonymous: 5, free: 5, premium: 5 },
  // Reading a job posting from its link (/api/job-posting/from-url). No AI,
  // but the server fetches a page for the visitor: enough for anyone applying,
  // too few to use it as a proxy.
  'posting-url': { anonymous: 20, free: 40, premium: 200 },
  // Reading an old CV or a LinkedIn PDF into text for the chat builder
  // (/api/cv-chat/file). No AI, only the PDF parser: a few files for retries
  // and typos, too few to use it as a free PDF-to-text service.
  'cv-file': { anonymous: 10, free: 10, premium: 50 },
  // Openings of the links for one company (/api/r/<token>/view), per visitor:
  // a recruiter reading twenty CVs a day stays far below it; a script
  // inflating somebody's "opened" count does not get far.
  'link-view': { anonymous: 100, free: 100, premium: 100 },
}

/** Lifetime allowances of a free account, and how much of a report it sees. */
export const FREE_PLAN = {
  /**
   * CVs a free account keeps (not deleted). A new one past this is Premium:
   * server/utils/cvLimit.ts and the cv_requests trigger in
   * supabase/migrations/20261017_free_cv_limit.sql (which repeats the number).
   * The one free translation (/api/cv/translate) is the exception: its copy
   * may be a second CV.
   */
  cvs: 1,
  /** Whole-CV translations in total (/api/cv/translate). */
  translations: 1,
  /** Saved cover letters in total (/api/cover-letters/generate)… */
  coverLetters: 1,
  /** …or this many generations, whichever runs out first. */
  coverLetterDrafts: 3,
  /** Findings per list in the free job match and ATS reports (server/utils/aiReport.ts). */
  reportItems: 3,
} as const

/** Applications one account can keep in the tracker, on every plan. */
export const MAX_APPLICATIONS = 1000
